/**
 * Azioni del motore e-commerce: tutte passano dal core (16 sez. 3.2), mai scritture dirette su commerce_*
 * dal browser e mai token o chiavi nel browser. Le letture stanno in queries.ts (Supabase con RLS).
 * Ogni funzione restituisce { ok, data } oppure { ok: false, code, message } con il codice stabile del
 * core: il testo per l'utente lo sceglie errorText.ts.
 */
import { coreCall } from '../services/gatewayService';
import { failureCode } from './coreRouting';
import type { ConfirmChoicesBody } from './connectChoices';
import type { CommerceProvider, ConnectRequestStatus, ConnectRequestView, StoreStatus } from './types';

export type ApiResult<T> = { ok: true; data: T } | { ok: false; code: string; message: string };

type Method = 'GET' | 'POST' | 'DELETE';

/** Chiamata al core con codice d'errore stabile (usata anche da adminApi.ts). */
export async function call<T>(name: string, method: Method, body?: Record<string, unknown>, idempotencyKey?: string): Promise<ApiResult<T>> {
  const { data, error } = await coreCall<T>(name, {
    method,
    ...(body ? { body } : {}),
    ...(idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {}),
  });
  if (error) return { ok: false, code: failureCode(error.code, error.status), message: error.message };
  return { ok: true, data: data as T };
}

const id = (v: string) => encodeURIComponent(v);

/**
 * Rotta della conferma del collegamento (fase 2 del collegamento, 16 sez. 5.5, api/confirmRoutes.ts del
 * core). Con il motore in fase 1 il core risponde not_ready e l'interfaccia lo dice in chiaro.
 */
export const CONFIRM_ROUTE = (storeId: string) => `commerce/stores/${storeId}/confirm`;

/** Chiave di idempotenza per le azioni che creano lavori (lo stesso clic ripetuto = stesso lavoro). */
export function newIdempotencyKey(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `k-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

export const commerceApi = {
  // ---- collegamento (fase 1) ----
  createConnectRequest: (i: { provider: 'shopify'; shopDomain: string } | { provider: 'woocommerce'; siteUrl: string }) =>
    call<{ request_id: string; status: ConnectRequestStatus }>('commerce/connect-requests', 'POST',
      i.provider === 'shopify' ? { provider: 'shopify', shop_domain: i.shopDomain } : { provider: 'woocommerce', site_url: i.siteUrl }),
  getConnectRequest: (requestId: string) => call<ConnectRequestView>(`commerce/connect-requests/${id(requestId)}`, 'GET'),
  cancelConnectRequest: (requestId: string) => call<{ ok: true }>(`commerce/connect-requests/${id(requestId)}`, 'DELETE'),
  authorize: (provider: CommerceProvider, target: { requestId: string } | { storeId: string }) =>
    call<{ url: string }>(`commerce/${provider}/authorize`, 'POST',
      'requestId' in target ? { request_id: target.requestId } : { store_id: target.storeId }),

  // ---- negozio ----
  // App Shopify pubblica (ecommerce/22): negozio che si sta collegando e approvazione per l'org attiva
  shopifyLinkPeek: (ticket: string) => call<{ shop_domain: string; status: 'open' | 'approved' }>(`commerce/shopify/link?t=${encodeURIComponent(ticket)}`, 'GET'),
  shopifyLinkApprove: (ticket: string) => call<{ shop_domain: string }>('commerce/shopify/link', 'POST', { ticket }),
  pluginPairingCode: (storeId: string) => call<{ code: string; expires_at: string }>(`commerce/stores/${id(storeId)}/plugin/pairing-code`, 'POST', {}),
  pauseStore: (storeId: string) => call<{ status: StoreStatus }>(`commerce/stores/${id(storeId)}/pause`, 'POST', {}),
  resumeStore: (storeId: string) => call<{ status: StoreStatus; job_id: string | null }>(`commerce/stores/${id(storeId)}/resume`, 'POST', {}),
  disconnectStore: (storeId: string, confirmName: string) =>
    call<{ status: 'disconnected' }>(`commerce/stores/${id(storeId)}/disconnect`, 'POST', { confirm_name: confirmName }),
  healthCheck: (storeId: string, key: string) => call<{ job_id: string }>(`commerce/stores/${id(storeId)}/health-check`, 'POST', {}, key),
  syncNow: (storeId: string, key: string, resources?: Array<'orders' | 'customers' | 'products'>) =>
    call<{ job_id: string }>(`commerce/stores/${id(storeId)}/sync-now`, 'POST', resources?.length ? { resources } : {}, key),
  startAnalysis: (storeId: string, key: string) => call<{ job_id: string }>(`commerce/stores/${id(storeId)}/analysis`, 'POST', {}, key),
  applySettings: (storeId: string, baseVersion: number, changes: Record<string, unknown>) =>
    call<{ applied: true; version: number }>(`commerce/stores/${id(storeId)}/settings`, 'POST', { base_version: baseVersion, changes }),

  // ---- lavori ed eventi ----
  jobAction: (jobId: string, action: 'pause' | 'resume' | 'cancel') => call<{ status: string }>(`commerce/jobs/${id(jobId)}/${action}`, 'POST', {}),
  retryFailed: (jobId: string) => call<{ job_id: string }>(`commerce/jobs/${id(jobId)}/retry-failed`, 'POST', {}),
  retryEvent: (eventId: string) => call<{ ok: true }>(`commerce/events/${id(eventId)}/retry`, 'POST', {}),

  // ---- anteprima dell'effetto e conferma (fase 2, 08 sez. 8) ----
  previewSettings: (storeId: string, changes: Record<string, unknown>) =>
    call<{ preview_id: string; job_id: string }>(`commerce/stores/${id(storeId)}/settings/preview`, 'POST', { changes }),
  /** Risposta grezza: la legge preview.ts (parsePreview). */
  getPreview: (previewId: string) => call<unknown>(`commerce/previews/${id(previewId)}`, 'GET'),
  confirmPreview: (previewId: string, planHash: string, confirmName?: string | null) =>
    call<{ job_id: string | null }>(`commerce/previews/${id(previewId)}/confirm`, 'POST',
      confirmName ? { plan_hash: planHash, confirm_name: confirmName } : { plan_hash: planHash }),

  // ---- conflitti, abbinamenti incerti, riprova (fase 2) ----
  resolveConflicts: (i: { refIds: string[]; group: string; choice: 'flusia' | 'store'; always?: boolean }) =>
    call<{ ok: true } | { preview_id: string }>('commerce/conflicts/resolve', 'POST', {
      ref_ids: i.refIds, group: i.group, choice: i.choice, ...(i.always ? { always: true } : {}),
    }),
  resolveDecision: (refId: string, target: { clientId: string } | { productId: string }) =>
    call<{ ok: true; moved_orders: number }>(`commerce/decisions/${id(refId)}/resolve`, 'POST',
      'clientId' in target ? { client_id: target.clientId } : { product_id: target.productId }),
  retryOutbox: (ids: string[]) => call<{ retried: number }>('commerce/outbox/retry', 'POST', { ids }),
  /** Prodotti messi (true) o tolti (false) dalla vendita in un negozio; null = seguono la regola del negozio. */
  setListings: (storeId: string, productIds: string[], listed: boolean | null) =>
    call<{ changed: number; queued: number }>(`commerce/stores/${id(storeId)}/listings`, 'POST', { product_ids: productIds, listed }),

  // ---- conferma del collegamento, passi 4-5 del guidato (connect/confirm.ts del core) ----
  /** Riepilogo in sola lettura delle scelte: risposta grezza, la legge connectChoices.ts (parseConfirmPlan). */
  previewConnection: (storeId: string, choices: ConfirmChoicesBody, copyFromStoreId: string | null = null) =>
    call<unknown>(`${CONFIRM_ROUTE(id(storeId))}/preview`, 'POST', { choices, ...(copyFromStoreId ? { copy_from_store_id: copyFromStoreId } : {}) }),
  /**
   * Conferma: nome del negozio scritto e versione delle impostazioni vista nel riepilogo (parseConfirmOutcome).
   * copyFromStoreId: impostazioni ed eccezioni dei prodotti copiate da quel negozio (connect/copySettings.ts del core).
   */
  confirmConnection: (storeId: string, choices: ConfirmChoicesBody, confirmName: string, baseVersion: number | null, key: string, copyFromStoreId: string | null = null) =>
    call<unknown>(CONFIRM_ROUTE(id(storeId)), 'POST', {
      choices, confirm_name: confirmName,
      ...(baseVersion !== null ? { base_version: baseVersion } : {}),
      ...(copyFromStoreId ? { copy_from_store_id: copyFromStoreId } : {}),
    }, key),

  // ---- freni scattati (fase 2) ----
  continueBrake: (storeId: string, brakeEventId: string) =>
    call<{ released: number }>(`commerce/brakes/${id(storeId)}/continue`, 'POST', { brake_event_id: brakeEventId }),
  cancelBrake: (storeId: string, brakeEventId: string) =>
    call<{ cancelled: number; restore_preview_id?: string }>(`commerce/brakes/${id(storeId)}/cancel`, 'POST', { brake_event_id: brakeEventId }),

  // ---- ripristino, annullo, esportazione (fase 2): l'anteprima si segue con getPreview ----
  restorePreview: (storeId: string, body: Record<string, unknown>) =>
    call<{ preview_id: string; job_id: string | null }>(`commerce/stores/${id(storeId)}/restore/preview`, 'POST', body),
  undoPreview: (jobId: string) => call<{ preview_id: string; job_id: string | null }>(`commerce/jobs/${id(jobId)}/undo/preview`, 'POST', {}),
  startExport: (storeId: string, what: string[], key: string) =>
    call<{ job_id: string }>(`commerce/stores/${id(storeId)}/export`, 'POST', { what }, key),
  exportUrl: (jobId: string) => call<{ url: string; expires_at: string }>(`commerce/exports/${id(jobId)}/url`, 'GET'),

  // ---- istantanee (fase 1: impostazioni e abbinamenti) ----
  createSnapshot: (storeId: string, name: string) =>
    call<{ snapshot_id: string; job_id: string }>(`commerce/stores/${id(storeId)}/snapshots`, 'POST', { name }),
  deleteSnapshot: (snapshotId: string) => call<{ ok: true }>(`commerce/snapshots/${id(snapshotId)}`, 'DELETE'),
};
